// frontend/src/config/metadata-systems.js

/**
 * METADATA_SYSTEMS — single source of truth for the Metadata page ERP tabs.
 *
 * The Metadata page (MetadataHelp.js) fetches metadata-headers.json from S3.
 * Each category in that file may carry an `erp_source` property (e.g. "lawson",
 * "landmark"). This config maps each erp_source to:
 *   - how its tab is labeled / ordered, and
 *   - which Athena named query + column set to use when a category is selected.
 *
 * Columns/queries live here (NOT in metadata-headers.json) so categories in the
 * JSON stay lightweight — most categories in one ERP share the same column shape.
 * Per-category specifics (KEY1/KEY2/KEY3) still come from the JSON and are
 * appended automatically by MetadataHelp.js.
 *
 * ─── FIELDS ────────────────────────────────────────────────────────────────
 *   id         — unique identifier / React key
 *   label      — tab label shown to the user
 *   sourceKey  — must match the `erp_source` value in metadata-headers.json
 *                (matched case-insensitively)
 *   namedQuery — the Athena named query run when a category is selected
 *                (passed as `reportName` to DashboardAthenaTable)
 *   paramName  — key used by AthenaParameterTransformation to shape query params
 *                (defaults to 'meta_data_read_temp', which maps to { prm_category })
 *   columns    — base column definitions for this ERP. Each column:
 *                  { accessorKey, header, type?, innerSearch?, enableSorting?, filterFn? }
 *                Use `type: 'date'` to render a date-range filter column.
 *
 * ─── ADDING A NEW ERP SYSTEM ───────────────────────────────────────────────
 *   1. Make sure categories in metadata-headers.json carry the new `erp_source`.
 *   2. Append a new entry below with its label, namedQuery, and columns.
 *   3. Ensure the namedQuery exists in the query system / Athena.
 *   The new tab appears automatically for any user with allowed categories in it.
 *
 * ─── BACKWARD COMPATIBILITY ────────────────────────────────────────────────
 *   Older metadata-headers.json files have no `erp_source`. MetadataHelp.js
 *   treats those categories as DEFAULT_METADATA_SOURCE, so the page renders
 *   exactly like before (a single source, no tabs). Any erp_source not listed
 *   here falls back to Lawson-style defaults via getMetadataSystem().
 */

export const DEFAULT_METADATA_SOURCE = 'lawson'

const DEFAULT_PARAM_NAME = 'meta_data_read_temp'

// Original hardcoded Lawson column set (preserved from MetadataHelp.js).
export const DEFAULT_METADATA_COLUMNS = [
  { accessorKey: 'CODE', header: 'Code', enableSorting: true },
  { accessorKey: 'DESCRIPTION', header: 'Description', innerSearch: true },
  { accessorKey: 'START_DATE', header: 'Start Date', type: 'date' },
  { accessorKey: 'END_DATE', header: 'End Date', type: 'date' },
  { accessorKey: 'ERP_STATUS', header: 'Status', enableSorting: true },
]

export const METADATA_SYSTEMS = [
  {
    id: 'lawson',
    label: 'Lawson',
    sourceKey: 'lawson',
    namedQuery: 'meta_data_read_temp',
    paramName: DEFAULT_PARAM_NAME,
    columns: DEFAULT_METADATA_COLUMNS,
  },
  {
    id: 'landmark',
    label: 'Infor GHR',
    sourceKey: 'landmark',
    namedQuery: 'lm_meta_data_read_temp',
    paramName: DEFAULT_PARAM_NAME,
    columns: [
      { accessorKey: 'CODE', header: 'Code', enableSorting: true },
      { accessorKey: 'DESCRIPTION', header: 'Description', innerSearch: true },
      { accessorKey: 'HISTORY', header: 'History', enableSorting: true },
      { accessorKey: 'ERP_STATUS', header: 'ERP Status', enableSorting: true },
    ],
  },
]

const normalize = (s) => String(s || '').toLowerCase()

/**
 * Resolve a metadata system config from an erp_source value.
 * Unknown sources fall back to Lawson-style defaults so the page never breaks
 * on an older or unexpected metadata-headers.json.
 */
export const getMetadataSystem = (sourceKey) => {
  const key = normalize(sourceKey) || DEFAULT_METADATA_SOURCE
  const found = METADATA_SYSTEMS.find((s) => normalize(s.sourceKey) === key)
  if (found) return found
  return {
    id: key,
    label: sourceKey || DEFAULT_METADATA_SOURCE,
    sourceKey: key,
    namedQuery: DEFAULT_PARAM_NAME,
    paramName: DEFAULT_PARAM_NAME,
    columns: DEFAULT_METADATA_COLUMNS,
  }
}

/** Sort comparator that orders systems by their position in METADATA_SYSTEMS. */
export const byMetadataConfigOrder = (a, b) => {
  const ai = METADATA_SYSTEMS.findIndex((s) => normalize(s.sourceKey) === normalize(a.sourceKey))
  const bi = METADATA_SYSTEMS.findIndex((s) => normalize(s.sourceKey) === normalize(b.sourceKey))
  return (ai === -1 ? Infinity : ai) - (bi === -1 ? Infinity : bi)
}

/** Normalized erp_source for a category, defaulting when the field is absent. */
export const categorySource = (category) =>
  normalize(category?.erp_source || category?.ERP_SOURCE) || DEFAULT_METADATA_SOURCE
